/**
 * User Activity Chart Component
 *
 * Displays top 10 users by activity using a grouped bar chart.
 * Shows session count and token usage for each user.
 */

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { UserActivityStats } from '../../../../../types/metrics';

interface UserActivityChartProps {
  data: UserActivityStats[];
}

export const UserActivityChart: React.FC<UserActivityChartProps> = ({ data }) => {
  // Take top 10 users by session count
  const top10Users = [...data].sort((a, b) => b.session_count - a.session_count).slice(0, 10);

  // Format data for recharts
  const chartData = top10Users.map((user) => ({
    name: user.user_name.split(' ')[0], // First name only for readability
    sessions: user.session_count,
    tokensK: Math.round(user.tokens_used / 1000), // Convert to thousands
  }));

  return (
    <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] h-[442px]">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">Atividade de Usuários</h3>
        <p className="text-sm text-[#888888]">Top 10 usuários por sessões</p>
      </div>

      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={330}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333333" />
            <XAxis dataKey="name" stroke="#888888" style={{ fontSize: '12px' }} />
            <YAxis stroke="#888888" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333333',
                borderRadius: '8px',
                color: '#ffffff',
              }}
              labelStyle={{ color: '#cccccc' }}
            />
            <Legend
              wrapperStyle={{ color: '#888888', fontSize: '12px' }}
              formatter={(value) => {
                if (value === 'sessions') return 'Sessões';
                if (value === 'tokensK') return 'Tokens (K)';
                return value;
              }}
            />
            <Bar dataKey="sessions" fill="#3b82f6" radius={[4, 4, 0, 0]} animationDuration={500} />
            <Bar dataKey="tokensK" fill="#F4CC04" radius={[4, 4, 0, 0]} animationDuration={500} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex items-center justify-center h-[330px] text-[#666666]">
          Nenhuma atividade de usuário no período
        </div>
      )}
    </div>
  );
};
